import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Feather, Plus, BookOpen, LogOut, Loader2, Layers } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", subtitle: "", author_name: "", language: "es", genre: "", description: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      setProjects([]);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (user?.name) setForm((f) => ({ ...f, author_name: f.author_name || user.name, language: lang })); }, [user, lang]);

  const createBook = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/projects", form);
      toast.success(t("toast.saved"));
      navigate(`/studio/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-900 grain">
      <header className="border-b border-white/5 bg-ink-900/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2.5">
            <Feather className="w-5 h-5 text-gold" />
            <span className="font-serif text-xl font-bold text-slate-100">{t("app.name")}</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <div className="hidden sm:flex items-center gap-2 pl-2">
              <div className="w-8 h-8 rounded-full bg-gold/15 flex items-center justify-center text-gold text-sm font-semibold">
                {user?.name?.[0]?.toUpperCase()}
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/"); }} className="text-slate-400 hover:text-red-400" data-testid="logout-btn" title={t("dash.logout")}>
              <LogOut className="w-4.5 h-4.5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-slate-100">{t("dash.title")}</h1>
            <p className="mt-1 text-sm text-slate-400">{t("dash.subtitle")}</p>
          </div>
          <Button onClick={() => setOpen(true)} className="bg-gold hover:bg-gold-light text-ink-900 font-semibold rounded-full h-11 px-6" data-testid="dash-new-book-btn">
            <Plus className="w-4 h-4 mr-1.5" /> {t("dash.new")}
          </Button>
        </div>

        {projects === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map((i) => <div key={i} className="h-56 rounded-2xl shimmer" />)}
          </div>
        ) : projects.length === 0 ? (
          <div className="card-surface rounded-2xl p-14 text-center mt-6" data-testid="dash-empty">
            <div className="w-14 h-14 rounded-2xl bg-gold/10 flex items-center justify-center mx-auto mb-5">
              <BookOpen className="w-7 h-7 text-gold" />
            </div>
            <h3 className="font-serif text-2xl font-semibold text-slate-100">{t("dash.empty.title")}</h3>
            <p className="mt-2 text-sm text-slate-400 max-w-sm mx-auto">{t("dash.empty.sub")}</p>
            <Button onClick={() => setOpen(true)} className="mt-6 bg-gold hover:bg-gold-light text-ink-900 font-semibold rounded-full h-11 px-6">
              <Plus className="w-4 h-4 mr-1.5" /> {t("dash.new")}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="sidebar-project-list">
            {projects.map((p, i) => (
              <motion.button
                key={p.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                onClick={() => navigate(`/studio/${p.id}`)}
                data-testid={`project-card-${p.id}`}
                className="card-surface rounded-2xl overflow-hidden text-left hover:border-gold/40 transition-all hover:-translate-y-0.5 group"
              >
                <div className="aspect-[3/2] bg-ink-800 relative overflow-hidden">
                  {p.cover_image ? (
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-ink-700 to-ink-800">
                      <BookOpen className="w-10 h-10 text-gold/30" />
                    </div>
                  )}
                  <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider bg-ink-900/80 text-gold px-2 py-0.5 rounded-full border border-gold/20">
                    {p.language}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl font-semibold text-slate-100 line-clamp-1 group-hover:text-gold transition-colors">{p.title}</h3>
                  {p.genre && <p className="text-xs text-gold/70 mt-0.5">{p.genre}</p>}
                  <div className="flex items-center gap-1.5 mt-3 text-xs text-slate-500">
                    <Layers className="w-3.5 h-3.5" /> {p.chapter_count} {t("dash.chapters")}
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-ink-800 border-white/10 text-slate-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{t("modal.newbook.title")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={createBook} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-400">{t("modal.field.title")}</Label>
              <Input data-testid="newbook-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="bg-ink-900 border-white/10 focus-visible:ring-gold" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-400">{t("modal.field.author")}</Label>
                <Input value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} className="bg-ink-900 border-white/10 focus-visible:ring-gold" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-400">{t("modal.field.language")}</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger className="bg-ink-900 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ink-800 border-white/10 text-slate-100">
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-400">{t("modal.field.genre")}</Label>
              <Input value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} className="bg-ink-900 border-white/10 focus-visible:ring-gold" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-400">{t("modal.field.description")}</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="bg-ink-900 border-white/10 focus-visible:ring-gold resize-none" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">{t("modal.cancel")}</Button>
              <Button type="submit" disabled={creating} className="bg-gold hover:bg-gold-light text-ink-900 font-semibold" data-testid="newbook-create-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : t("modal.create")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
